import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Formik, Form } from 'formik'
import * as yup from 'yup'
import VerificationCodeInput from 'react-input-verification-code'
import { useTheme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import LoadingButton from '@mui/lab/LoadingButton'
import Stack from '@mui/material/Stack'

import TextField from '@/components/formik/fields/TextField'

import { StepBaseProps } from '../types'

export type ConfirmEmailStepValues = {
  email: string
}

export type ConfirmEmailStepProps = StepBaseProps & {
  initialValues: ConfirmEmailStepValues
  onSendEmail: (email: string) => void
  onVerifyEmail: (code: string) => void
  status: 'initial' | 'pending'
}

const verificationCodeLength = 6

export function ConfirmEmailStep(props: ConfirmEmailStepProps) {
  const { initialValues, isSubmitting = false, onSendEmail, onVerifyEmail, status = 'initial' } = props
  const { t } = useTranslation()
  const theme = useTheme()

  const [code, setCode] = useState<string>('')

  const handleSubmit = (values: ConfirmEmailStepValues) => {
    onSendEmail(values.email)
  }

  const handleVerifyEmail = () => {
    onVerifyEmail(code)
  }

  return (
    <Box>
      {status === 'initial' && (
        <Formik
          enableReinitialize
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={yup.object().shape({
            email: yup.string().email().required()
          })}
        >
          {({ isValid }) => (
            <Form>
              <Stack spacing={4}>
                <TextField label={t('field.email.label')} name="email" />
                <LoadingButton disabled={!isValid} loading={isSubmitting} type="submit" variant="contained">
                  {t('common:button.send.label')}
                </LoadingButton>
              </Stack>
            </Form>
          )}
        </Formik>
      )}
      {status === 'pending' && (
        <Stack spacing={4} alignItems="center">
          <Box
            sx={{
              margin: '0 auto',
              '--ReactInputVerificationCode-itemWidth': '30px',
              '--ReactInputVerificationCode-itemHeight': '30px',
              '--ReactInputVerificationCode-itemSpacing': '10px',
              [theme.breakpoints.up('sm')]: {
                '--ReactInputVerificationCode-itemWidth': '50px',
                '--ReactInputVerificationCode-itemHeight': '50px',
                '--ReactInputVerificationCode-itemSpacing': '16px'
              }
            }}
          >
            <VerificationCodeInput
              autoFocus
              length={verificationCodeLength}
              onChange={setCode}
              value={code}
            />
          </Box>
          <LoadingButton disabled={code.includes('·')} loading={isSubmitting} onClick={handleVerifyEmail} variant="contained">
            {t('common:button.verify.label')}
          </LoadingButton>
        </Stack>
      )}
    </Box>
  )
}

export default ConfirmEmailStep